import { useState, useEffect } from 'react';
import {
  Modal,
  Text,
  Button,
  Stack,
  Group,
  Card,
  Badge,
  ActionIcon,
  TextInput,
  ScrollArea,
  Alert,
  Loader,
  Tooltip,
} from '@mantine/core';
import {
  IconFolder,
  IconTrash,
  IconDownload,
  IconCalendar,
  IconFile,
  IconAlertCircle,
  IconSearch,
  IconFolderOpen,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import {
  getSavedProjects,
  deletePDFProject,
  clearAllProjects,
  getProjectsStorageUsage,
  type SavedPDFProject,
} from '../../utils/storage/pdfProjects';
import { useTranslation } from '../../contexts/hooks';

interface SavedProjectsModalProps {
  opened: boolean;
  onClose: () => void;
  onLoadProject: (project: SavedPDFProject) => void;
}

export function SavedProjectsModal({ opened, onClose, onLoadProject }: SavedProjectsModalProps) {
  const { t } = useTranslation();
  const [projects, setProjects] = useState<SavedPDFProject[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [storageUsage, setStorageUsage] = useState({ size: 0, count: 0 });

  // Refresh projects when modal opens
  useEffect(() => {
    if (opened) {
      refreshProjects();
      loadStorageUsage();
    }
  }, [opened]);

  const refreshProjects = async () => {
    try {
      const projects = await getSavedProjects();
      setProjects(projects);
    } catch (error) {
      console.error('Failed to refresh projects:', error);
      setProjects([]);
    }
  };

  const loadStorageUsage = async () => {
    try {
      const usage = await getProjectsStorageUsage();
      setStorageUsage(usage);
    } catch (error) {
      console.error('Failed to load storage usage:', error);
      setStorageUsage({ size: 0, count: 0 });
    }
  };

  const handleDeleteProject = async (projectId: string) => {
    setIsLoading(true);
    try {
      const success = await deletePDFProject(projectId);
      if (success) {
        await refreshProjects();
        await loadStorageUsage();
        notifications.show({
          title: t('projects.delete.success'),
          message: t('projects.delete.success.detail'),
          color: 'green',
        });
      } else {
        throw new Error('Failed to delete project');
      }
    } catch {
      notifications.show({
        title: t('projects.delete.error'),
        message: t('projects.delete.error.detail'),
        color: 'red',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearAllProjects = async () => {
    setIsLoading(true);
    try {
      const success = await clearAllProjects();
      if (success) {
        await refreshProjects();
        await loadStorageUsage();
        notifications.show({
          title: t('projects.deleteall.success'),
          message: t('projects.deleteall.success.detail'),
          color: 'green',
        });
      } else {
        throw new Error('Failed to clear all projects');
      }
    } catch {
      notifications.show({
        title: t('projects.deleteall.error'),
        message: t('projects.deleteall.error.detail'),
        color: 'red',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoadProject = async (project: SavedPDFProject) => {
    setIsLoading(true);
    try {
      onLoadProject(project);
      onClose();
    } catch {
      notifications.show({
        title: t('projects.load.error'),
        message: t('projects.load.error.detail'),
        color: 'red',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const filteredProjects = projects.filter(project =>
    project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    project.originalFileName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Helper function for pluralization
  const getPlural = (count: number, singular: string, plural?: string) => {
    return count !== 1 ? (plural || singular + 's') : '';
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t('projects.modal.title')}
      size="lg"
      scrollAreaComponent={ScrollArea.Autosize}
    >
      <Stack gap="md">
        {/* Storage Info */}
        <Alert icon={<IconAlertCircle size={16} />} color="blue" variant="light">
          <Group justify="space-between">
            <Text size="sm">
              {t('projects.count')
                .replace('{count}', storageUsage.count.toString())
                .replace('{plural}', getPlural(storageUsage.count, ''))
                .replace('{size}', formatFileSize(storageUsage.size))}
            </Text>
            {projects.length > 0 && (
              <Button
                size="xs"
                variant="light"
                color="red"
                onClick={handleClearAllProjects}
                disabled={isLoading}
              >
                {t('projects.deleteall.button')}
              </Button>
            )}
          </Group>
        </Alert>

        {/* Search */}
        <TextInput
          placeholder={t('projects.list.search.placeholder')}
          leftSection={<IconSearch size={16} />}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.currentTarget.value)}
        />

        {/* Projects List */}
        {isLoading && (
          <Group justify="center" p="xl">
            <Loader size="sm" />
            <Text size="sm">{t('projects.loading')}</Text>
          </Group>
        )}

        {!isLoading && filteredProjects.length === 0 && projects.length === 0 && (
          <Card withBorder p="xl">
            <Stack align="center" gap="sm">
              <IconFolderOpen size={48} color="gray" />
              <Text size="lg" fw={500} c="dimmed">
                {t('projects.list.empty')}
              </Text>
              <Text size="sm" c="dimmed" ta="center">
                {t('projects.list.empty.detail')}
              </Text>
            </Stack>
          </Card>
        )}

        {!isLoading && filteredProjects.length === 0 && projects.length > 0 && (
          <Card withBorder p="xl">
            <Stack align="center" gap="sm">
              <IconSearch size={48} color="gray" />
              <Text size="lg" fw={500} c="dimmed">
                {t('projects.list.search.noresults')}
              </Text>
              <Text size="sm" c="dimmed">
                {t('projects.list.search.noresults.detail')}
              </Text>
            </Stack>
          </Card>
        )}

        {!isLoading && filteredProjects.map((project) => (
          <Card key={project.id} withBorder>
            <Stack gap="sm">
              <Group justify="space-between" align="flex-start">
                <Stack gap={4} style={{ flex: 1 }}>
                  <Group gap="xs">
                    <IconFolder size={16} />
                    <Text fw={500} size="sm">
                      {project.name}
                    </Text>
                  </Group>
                  <Group gap="xs">
                    <IconFile size={14} color="gray" />
                    <Text size="xs" c="dimmed">
                      {project.originalFileName}
                    </Text>
                  </Group>
                </Stack>
                <Group gap="xs">
                  <Tooltip label={t('projects.load.tooltip')}>
                    <ActionIcon
                      variant="light"
                      color="blue"
                      onClick={() => handleLoadProject(project)}
                      disabled={isLoading}
                    >
                      <IconDownload size={16} />
                    </ActionIcon>
                  </Tooltip>
                  <Tooltip label={t('projects.delete.tooltip')}>
                    <ActionIcon
                      variant="light"
                      color="red"
                      onClick={() => handleDeleteProject(project.id)}
                      disabled={isLoading}
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
              </Group>

              <Group gap="xs">
                <Badge size="xs" variant="light">
                  {t('projects.pages')
                    .replace('{count}', project.totalPages.toString())
                    .replace('{plural}', getPlural(project.totalPages, ''))}
                </Badge>
                <Badge size="xs" variant="light" color="green">
                  {t('projects.fields')
                    .replace('{count}', (project.fields.length + project.existingFields.length).toString())
                    .replace('{plural}', getPlural(project.fields.length + project.existingFields.length, ''))}
                </Badge>
              </Group>

              <Group gap="xs" align="center">
                <IconCalendar size={14} color="gray" />
                <Text size="xs" c="dimmed">
                  {t('projects.modified').replace('{date}', formatDate(project.lastModified))}
                </Text>
              </Group>
            </Stack>
          </Card>
        ))}
      </Stack>
    </Modal>
  );
}